import { HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ExpandListConfiguration, SnackbarService } from 'ok-angular-shared/components';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { ChargingStationLocationRelationService } from 'src/app/shared/services/charging-station-location-relation.service';
import { ChargingStationLocation, ChargingStationLocationDisplay } from './charging-station-location-model';
import {
  ChargingStationLocationDefaultsDialogData,
  ChargingStationLocationDefaultsModalComponent,
} from './charging-station-location-defaults-modal/charging-station-location-defaults-modal.component';
import {
  MultipleConnectorPricesPolicyDialogComponent,
  MultipleConnectorPricesPolicyDialogData,
} from './multiple-connector-prices-policy-dialog/multiple-connector-prices-policy-dialog.component';
import { ChargingStationLocationCreateEditModalComponent } from './charging-station-location-create-modal/charging-station-location-create-edit-modal';
import { ChargingStationLocationDialogData } from './charging-station-location-create-modal/charging-station-location-created-edit-dialog.service';
import { ConnectPriceRegulationDialogComponent } from './connect-price-regulation-dialog/connect-price-regulation-dialog.component';
import { ConnectPriceRegulationModalData } from './charging-station-location-price-regulation.model';
import { AuthService } from 'src/app/core/auth/auth.service';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { Permission } from 'src/app/shared/enums/permission.enum';

@Service()
export class ChargingStationLocationActionsService {
  private readonly dialog = inject(MatDialog);
  private readonly chargingStationLocationRelationService = inject(ChargingStationLocationRelationService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly authService = inject(AuthService);
  private readonly environmentLoaderService = inject(EnvironmentLoaderService);

  createLocationActions(
    onLocationUpdated: (locationIdentifier: string) => void
  ): NonNullable<ExpandListConfiguration<ChargingStationLocationDisplay>['actions']> {
    const roles = this.environmentLoaderService.virtualRoles;

    return [
      {
        svgIcon: 'keep',
        label: 'Idriftsæt lokation',
        action: item => item && this.setOperatingDate(item, () => onLocationUpdated(item.identifier)),
        hiddenFn: item => !item || !!item.operatingDate || !this.authService.hasRole(roles[Permission.ChargingStationLocationWrite]),
      },
      {
        svgIcon: 'price_change',
        label: 'Prisregulering for adgangsgrupper',
        action: item => item && this.createOrEditPriceRegulation(item, () => onLocationUpdated(item.identifier)),
        hiddenFn: () => !this.authService.hasRole(roles[Permission.PriceRegulationWrite]),
      },
      {
        svgIcon: 'settings',
        label: 'Standardværdier for lokation',
        action: item => item && this.editChargingStationLocationDefault(item, () => onLocationUpdated(item.identifier)),
        hiddenFn: () => !this.authService.hasRole(roles[Permission.ChargingStationLocationDefaultsWrite]),
      },
      {
        svgIcon: 'sell_outlined',
        label: 'Prispolitik for alle ladepunkter',
        action: item => item && this.editPricePolicyForAllConnectors(item, () => onLocationUpdated(item.identifier)),
        hiddenFn: () => !this.authService.hasRole(roles[Permission.ChargingStationLocationPricePolicyWrite]),
      },
      {
        icon: { name: 'edit-s' },
        label: 'Rediger lokation',
        action: item => item && this.editChargingStationLocation(item, locationIdentifier => onLocationUpdated(locationIdentifier)),
        hiddenFn: () => !this.authService.hasRole(roles[Permission.ChargingStationLocationWrite]),
      },
    ];
  }

  setOperatingDate(location: ChargingStationLocation, onSuccess: () => void) {
    if (location.operatingDate) {
      return;
    }

    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Idriftsæt lokation?`,
        text: `Er du sikker på du vil idriftsætte lokation '${location.name}'?`,
        confirmText: `Ja`,
        denyText: `Nej`,
      },
      disableClose: true,
    });

    confirmDialogRef.afterClosed().subscribe(setOperatingDate => {
      if (setOperatingDate) {
        this.chargingStationLocationRelationService.setOperatingDate(location.identifier).subscribe({
          next: operatingDate => {
            location.operatingDate = operatingDate as Date;
            this.snackbarService.success('Lokationen blev succesfuldt markeret som idriftsat fra dette øjeblik.');
            onSuccess();
          },
          error: (err: HttpErrorResponse) => {
            this.snackbarService.error(`Der skete fejl under idriftsættelse: (${err.error.code})`);
          },
        });
      }
    });
  }

  editChargingStationLocationDefault(location: ChargingStationLocation, onSuccess: () => void) {
    const dialogRef = this.dialog.open(ChargingStationLocationDefaultsModalComponent, {
      data: {
        locationIdentifier: location.identifier,
        organizationId: location.organizationId,
        canHaveDynamicPricePolicy: location.canHaveDynamicPricePolicy,
      } satisfies ChargingStationLocationDefaultsDialogData,
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .subscribe(
        (result: { chargingStationLocationIdentifier: string; defaultPricePolicyName: string; defaultAccessGroups: unknown[] } | null) => {
          if (result?.chargingStationLocationIdentifier) {
            onSuccess();
          }
        }
      );
  }

  editChargingStationLocation(
    location: ChargingStationLocationDisplay,
    onSuccess: (chargingStationLocationIdentifier: string, location: ChargingStationLocationDisplay) => void
  ) {
    const dialogRef = this.dialog.open(ChargingStationLocationCreateEditModalComponent, {
      data: {
        locationIdentifier: location.identifier,
        location: location,
      } as ChargingStationLocationDialogData,
      disableClose: false,
      width: '650px',
    });

    dialogRef.afterClosed().subscribe((chargingStationLocationIdentifier: string | null) => {
      if (chargingStationLocationIdentifier) {
        onSuccess(chargingStationLocationIdentifier, location);
      }
    });
  }

  createOrEditPriceRegulation(location: ChargingStationLocationDisplay, onClosed: () => void) {
    this.dialog
      .open(ConnectPriceRegulationDialogComponent, {
        width: '900px',
        data: <ConnectPriceRegulationModalData>{
          organizationId: location.organizationId,
          organizationName: location.organizationName,
          locationIdentifier: location.identifier,
          locationName: location.name,
        },
        disableClose: false,
      })
      .afterClosed()
      .subscribe(onClosed);
  }

  editPricePolicyForAllConnectors(location: ChargingStationLocation, onSuccess: () => void) {
    this.dialog
      .open(MultipleConnectorPricesPolicyDialogComponent, {
        data: {
          chargingStationLocationIdentifier: location.identifier,
          canHaveDynamicPricePolicy: location.canHaveDynamicPricePolicy,
        } satisfies MultipleConnectorPricesPolicyDialogData,
        disableClose: true,
        width: '640px',
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          onSuccess();
        }
      });
  }
}
